import { defineStore } from 'pinia'
import { useAuthStore } from './auth'
import {
    getBasedContents,
    getBasedContent,
    createBasedContent,
    updateBasedContent,
    deleteBasedContent
} from '../services/api'
import type { BasedContent } from '../interfaces/basedComponents'

export const useWysiwygStore = defineStore('wysiwyg', {
    state: () => ({
        content: '',
        documents: [] as BasedContent[],
        currentDocument: null as BasedContent | null,
        isLoading: false,
        error: null as unknown | null,
    }),
    
    actions: {
        setContent(newContent: string) {
            this.content = newContent
        },

        async fetchDocuments() {
            this.isLoading = true;
            try {
                const res = await getBasedContents();
                this.documents = res.data.data;
                this.error = null;
            } catch (err) {
                this.error = err;
            } finally {
                this.isLoading = false;
            }
        },

        async fetchDocument(id: string) {
            this.isLoading = true;
            try {
                const res = await getBasedContent(id);
                this.currentDocument = res.data.data;
                this.content = this.currentDocument?.content_html || '';
                this.error = null;
            } catch (err) {
                this.error = err;
            } finally {
                this.isLoading = false;
            }
        },

     async createDocument(data: Record<string, unknown>) {
            this.isLoading = true;
            try {
                const payload = {
                    title: typeof data.title === 'string' && data.title.length > 0 ? data.title : 'Untitled',
                    status: typeof data.status === 'string' && data.status.length > 0 ? data.status : 'draft',
                    content_html: typeof data.content_html === 'string' ? data.content_html : '',
                    // Default to a valid Tiptap empty document object instead of an empty array
                    content_json: (data.content_json && typeof data.content_json === 'object'
                        ? data.content_json
                        : { type: 'doc', content: [] }) as Record<string, unknown>,
                    page_settings: (data.page_settings && typeof data.page_settings === 'object'
                        ? data.page_settings
                        : undefined) as Record<string, unknown> | undefined,
                };
                
                console.log('[wysiwyg] createDocument payload:', payload);
                const res = await createBasedContent(payload);
                this.currentDocument = res.data.data;
                
                if (this.currentDocument) {
                    this.documents.push(this.currentDocument);
                }
                this.error = null;
            } catch (err) {
                console.error('[wysiwyg] createDocument error:', (err as { response?: { data?: unknown } })?.response?.data || err);
                this.error = err;
                throw err;
            } finally {
                this.isLoading = false;
            }
        },

        async updateDocument(id: string, data: Record<string, unknown>) {
            this.isLoading = true;
            try {
                const payload = {
                    title: typeof data.title === 'string' && data.title.length > 0 ? data.title : 'Untitled',
                    status: typeof data.status === 'string' && data.status.length > 0 ? data.status : 'draft',
                    content_html: typeof data.content_html === 'string' ? data.content_html : '',
                    content_json: (data.content_json && typeof data.content_json === 'object'
                        ? data.content_json
                        : { type: 'doc', content: [] }) as Record<string, unknown>,
                    page_settings: (data.page_settings && typeof data.page_settings === 'object'
                        ? data.page_settings
                        : undefined) as Record<string, unknown> | undefined,
                };
                
                console.log('[wysiwyg] updateDocument payload:', payload);
                const res = await updateBasedContent(id, payload);
                this.currentDocument = res.data.data;
                
                // Update in documents array
                const idx = this.documents.findIndex(doc => doc.id === id);
                if (idx !== -1 && this.currentDocument) {
                    this.documents[idx] = this.currentDocument;
                }
                this.error = null;
            } catch (err) {
              console.error('[wysiwyg] updateDocument error:', (err as { response?: { data?: unknown } })?.response?.data || err);
                this.error = err;
                throw err;
            } finally {
                this.isLoading = false;
            }
        },

        async deleteDocument(id: string) {
            this.isLoading = true;
            try {
                await deleteBasedContent(id);
                this.documents = this.documents.filter(doc => doc.id !== id);
                if (this.currentDocument && this.currentDocument.id === id) {
                    this.currentDocument = null;
                    this.content = '';
                }
                this.error = null;
            } catch (err) {
                this.error = err;
            } finally {
                this.isLoading = false;
            }
        },
    },
    persist: {
        key: () => {
            // Use Pinia context to access auth store
            try {
                const authStore = useAuthStore()
                // Prefer user id, fallback to email, fallback to generic
                if (authStore?.user?.id) return `wysiwyg-${authStore.user.id}`
                if (authStore?.user?.email) return `wysiwyg-${authStore.user.email}`
            } catch {
                // ignore error (user not logged in yet)
            }
            return 'wysiwyg-guest'
        },
    }
})
